export const IMPERSONATION_STOP_FILTER_RESET_SESSION_KEY = 'impersonation-stop-filter-reset'
export const FILTER_STATE_IMPERSONATION_FIX_GATE =
  'fe-2887-filter-state-persists-after-stopping-impersonation'

/** Scopes persisted under the shared `additionalFilters` localStorage key. */
export const ADDITIONAL_FILTER_SCOPES = Object.freeze([
  'live',
  'completed',
  'command-center',
  'temperature',
  'workflows',
  'insights_temperature-release',
  'insights_lane-performance',
  'insights_command-center',
  'insights_product-release',
])

function clearAdditionalFiltersStorage() {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem('additionalFilters')
  } catch {
    // ignore private-mode / quota errors
  }
}

/** Clears shared filter blobs so a later page cannot rehydrate impersonated state. */
function clearPersistedFilterStorage() {
  if (typeof localStorage === 'undefined') return
  try {
    const keysToRemove = []
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i)
      if (!key) continue
      if (
        key === 'shipmentFilters' ||
        key.startsWith('shipmentFilters_') ||
        key === 'additionalFilters'
      ) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key))
  } catch {
    // ignore private-mode / quota errors
  }
}

function removeAdditionalFiltersScope(scopeKey) {
  if (typeof localStorage === 'undefined' || !scopeKey) return
  try {
    const raw = localStorage.getItem('additionalFilters')
    if (!raw) return
    const stored = JSON.parse(raw || '{}')
    if (!stored || typeof stored !== 'object' || !(scopeKey in stored)) return
    delete stored[scopeKey]
    if (Object.keys(stored).length === 0) {
      localStorage.removeItem('additionalFilters')
      return
    }
    localStorage.setItem('additionalFilters', JSON.stringify(stored))
  } catch {
    // ignore private-mode / quota / parse errors
  }
}

function readResetState() {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_STOP_FILTER_RESET_SESSION_KEY)
    if (!raw) return null
    // Legacy FE-2887 value: a single global pending flag.
    if (raw === '1') {
      return {
        scopes: [...ADDITIONAL_FILTER_SCOPES],
        blockFilterUiWrites: true,
      }
    }
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    const scopes = Array.isArray(parsed.scopes)
      ? parsed.scopes.filter((scope) => typeof scope === 'string' && scope.length > 0)
      : []
    return {
      scopes,
      blockFilterUiWrites: Boolean(parsed.blockFilterUiWrites),
    }
  } catch {
    return null
  }
}

function writeResetState(state) {
  if (typeof sessionStorage === 'undefined') return
  try {
    if (!state || (!state.blockFilterUiWrites && (!state.scopes || state.scopes.length === 0))) {
      sessionStorage.removeItem(IMPERSONATION_STOP_FILTER_RESET_SESSION_KEY)
      return
    }
    sessionStorage.setItem(
      IMPERSONATION_STOP_FILTER_RESET_SESSION_KEY,
      JSON.stringify({
        scopes: state.scopes || [],
        blockFilterUiWrites: Boolean(state.blockFilterUiWrites),
      }),
    )
  } catch {
    // ignore private-mode / quota errors
  }
}

/**
 * Called when impersonation stops. Deletes shared `additionalFilters` immediately so
 * a later page cannot rehydrate another user's selections after the first page
 * consumes its scope. Pending scopes stay marked until each page initializes.
 */
export function markImpersonationStopFilterReset() {
  // Delete immediately (before async session switch finishes) so the first page
  // that consumes its scope cannot leave stale entries for other pages.
  clearPersistedFilterStorage()
  writeResetState({
    scopes: [...ADDITIONAL_FILTER_SCOPES],
    blockFilterUiWrites: true,
  })
}

/**
 * @param {string} [scopeKey] When provided, only that additionalFilters scope is pending.
 *   When omitted, returns whether filter UI persistence should still be blocked
 *   (useFilterSystem / ShipmentFilters / LiveShipment write guards).
 */
export function isImpersonationStopFilterResetPending(scopeKey) {
  const state = readResetState()
  if (!state) return false
  if (typeof scopeKey === 'string' && scopeKey.length > 0) {
    return state.scopes.includes(scopeKey)
  }
  return state.blockFilterUiWrites
}

export function readAdditionalFiltersForScope(
  scopeKey,
  { preferFilterDetails = false, resetInProgress = false } = {},
) {
  if (!scopeKey || resetInProgress || isImpersonationStopFilterResetPending(scopeKey)) {
    if (scopeKey) removeAdditionalFiltersScope(scopeKey)
    return []
  }

  try {
    const stored = JSON.parse(localStorage.getItem('additionalFilters') || '{}')
    const entry = stored?.[scopeKey]
    const filters = entry?.filters
    const filterDetails = entry?.filter_details

    if (preferFilterDetails && Array.isArray(filterDetails) && filterDetails.length > 0) {
      return filterDetails
    }
    return Array.isArray(filters) ? filters : []
  } catch {
    return []
  }
}

export function writeAdditionalFiltersForScope(scopeKey, entry, { resetInProgress = false } = {}) {
  if (!scopeKey || resetInProgress || isImpersonationStopFilterResetPending(scopeKey)) {
    return false
  }

  try {
    const stored = JSON.parse(localStorage.getItem('additionalFilters') || '{}')
    localStorage.setItem(
      'additionalFilters',
      JSON.stringify({
        ...stored,
        [scopeKey]: entry,
      }),
    )
    return true
  } catch {
    return false
  }
}

/**
 * @param {string} [scopeKey] Consume one page's additionalFilters scope. Omitting
 *   scopeKey clears every remaining scope (legacy LiveShipment callers).
 *   The first consume also lifts the filter-UI write block so the active page can
 *   persist new selections.
 */
export function consumeImpersonationStopFilterReset(scopeKey) {
  const state = readResetState()
  if (!state) return false

  if (typeof scopeKey === 'string' && scopeKey.length > 0) {
    removeAdditionalFiltersScope(scopeKey)
    state.scopes = state.scopes.filter((scope) => scope !== scopeKey)
  } else {
    clearAdditionalFiltersStorage()
    state.scopes = []
  }

  state.blockFilterUiWrites = false
  writeResetState(state)
  return true
}
